import '@tableau/design-tokens/tokens-dark.min.css';
import '@tableau/design-tokens/tokens.min.css';

import React from 'react';
import { createTypeStyle, types } from 'typestyle';

export { classes } from 'typestyle';

export type Style = types.NestedCSSProperties;

export const ApplicationStylesTestId = 'ApplicationStyles';

const styleElement = document.createElement('style');
styleElement.setAttribute('data-tb-test-id', ApplicationStylesTestId);
let styleElementAppended = false;

const cssClassNameMap: { [className: string]: boolean } = {};

/** Creates a unique classname for the specified styles using TypeStyle */
export function cssClass(prefix: string, name: string, ...styles: Style[]): string {
  const instance = createTypeStyle();
  const cssClassName = instance.style(...styles, {
    $debugName: `${prefix}_${name}`,
    $unique: true,
  });

  if (!cssClassNameMap[cssClassName]) {
    // Some TypeStyle CSS classes are defined not directly in top level JavaScript code but latter as a function.
    // The purpose is to consume the CDN prefix which is not generated by top level JavaScript code.
    // E.g. getVizRecViewsIcon in CardViewStyles.ts
    // But it creates a problem that this kind of function may generated duplicated CSS rules which it's called for multiple times.
    // So now we need to check if a CSS Class name is already generated, if not, we add the related CSS rule to the page.
    cssClassNameMap[cssClassName] = true;

    // The following code ensures that the style tag for the VPC application styles won't be appended into document.head until any VPC feature code calls this function to declare CSS styles.
    // The reason is that we need to ensure the VPC application styles are declared after Shared Widgets styles (which is declared in top level JS code).
    // Otherwise Shared Widgets styles will override VPC application styles unexpectedly.
    if (!styleElementAppended) {
      styleElementAppended = true;
      document.head.appendChild(styleElement);
    }

    // in "stylesFromLegacyStylusCode" there are a lot of CSS rules migrated from the decommissioned Stylus CSS code.
    // Those CSS rules are all written into one <style> element which makes this element super big.
    // When this <style> element is very big, when adding new styles to it, it makes the webpage shows a short style refreshing for 0.5 second which causes some
    // HTML elements loose styles for a very short while.
    // So, for any CSS styles generated by this "cssClass" function, we need to put the styles into a separated <style> element with the following code:
    styleElement.innerHTML += instance.getStyles();
  }

  return cssClassName;
}

type Omit<T, K extends keyof T> = Pick<T, Exclude<keyof T, K>>;
export type OtherDivProps = Omit<React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement>, 'className'>;

export const errorNameContainsSpaces = 'DivWithStyle component name may not contain spaces';

export function divWithStyle(prefix: string, name: string, styles: Style | Style[], definedDivProps?: OtherDivProps):
  React.ComponentType<OtherDivProps> {

  if (name.indexOf(' ') > -1) {
    throw new Error(errorNameContainsSpaces);
  }

  if (!Array.isArray(styles)) {
    styles = [styles];
  }

  const className = cssClass(prefix, name, ...styles);

  return class extends React.Component<OtherDivProps> {
    public static displayName: string = name;

    public render() {
      const divPropsWithClassName = { ...definedDivProps, ...this.props, className };
      return (
        <div {...divPropsWithClassName}>
          {this.props.children}
        </div>
      );
    }
  };
}

export const fillStyle: Style = {
  position: 'absolute',
  left: 0,
  right: 0,
  top: 0,
  bottom: 0,
};

export const boxStyle: Style = {
  display: 'inline-block',
  verticalAlign: 'top',
};

export const flexColumnStyle: Style = {
  display: 'flex',
  flexDirection: 'column',
};

export const flexRowStyle: Style = {
  display: 'flex',
  flexDirection: 'row',
};

export const textEllipsis: Style = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap'
};

export const baseBackgroundImgIcon: Style = {
  backgroundPosition: 'center center',
  backgroundRepeat: 'no-repeat',
};

// This function should only be called in unit test code.
export function resetApplicationStylesElementStateForTestPurpose() {
  styleElementAppended = false;
}